import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { KeyboardArrowDown, Check } from '@mui/icons-material';

// Languages configuration
const languages = [
  { code: 'en', name: 'English', shortName: 'EN', flag: '🇺🇸' },
  { code: 'pl', name: 'Polski', shortName: 'PL', flag: '🇵🇱' },
  { code: 'ru', name: 'Русский', shortName: 'RU' },
];

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Get current language code
  const currentLang = i18n.language || 'en';

  // Find current language name
  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Change language handler
  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    handleClose();
  };

  return (
    <Box>
      <Button
        onClick={handleClick}
        endIcon={<KeyboardArrowDown sx={{ color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
        sx={{ 
          textTransform: 'none',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '20px',
          px: { xs: 1, sm: 2 },
          py: { xs: 0.5, sm: 1 },
          minWidth: 'auto',
          minHeight: { xs: '28px', sm: '40px' },
          cursor: 'pointer',
          WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
          touchAction: 'manipulation',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&:focus': {
            backgroundColor: 'action.hover',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {currentLanguage.flag && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              {currentLanguage.flag}
            </Typography>
          )}
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.primary',
              fontWeight: 500,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            {currentLanguage.shortName}
          </Typography>
        </Box>
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
            }
          }
        }}
      >
        {languages.map((language) => (
          <MenuItem 
            key={language.code} 
            onClick={() => changeLanguage(language.code)}
            selected={currentLang === language.code}
            sx={{
              backgroundColor: currentLang === language.code ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              border: currentLang === language.code ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid transparent',
              borderRadius: 1,
              margin: 0.5,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
              touchAction: 'manipulation',
              '&:hover': {
                backgroundColor: currentLang === language.code ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              {language.flag && (
                <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                  {language.flag}
                </Typography>
              )}
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {language.name}
              </Typography>
              {currentLang === language.code && (
                <Check sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher; 